// src/components/AddFirma.js
import React, { useState, useContext, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { useMediaQuery } from "react-responsive";
import { baseURL } from "../../utils/api";

const AddFirma = ({ onAddSuccess }) => {
  const toast = useRef(null);
  const logoInputRef = useRef(null);
  const editingLogoInputRef = useRef(null);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { user, token } = useContext(AuthContext);
  const [nazwa, setNazwa] = useState("");
  const [kodpocztowy, setKodpocztowy] = useState("");
  const [miasto, setMiasto] = useState("");
  const [ulica, setUlica] = useState("");
  const [numer, setNumer] = useState("");
  const [nip, setNip] = useState("");
  const [regon, setRegon] = useState("");
  // Lista firm – dodajemy także pole "dostepny" (domyślnie true)
  const [firmy, setFirmy] = useState([]);
  // Tryb edycji rekordu (dla możliwości zmiany statusu dostępności)
  const [editingFirma, setEditingFirma] = useState(null);
  // Kontrola widoczności – domyślnie wyświetlamy tylko dostępne firmy
  const [showAll, setShowAll] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editingLogoPreview, setEditingLogoPreview] = useState(null);

  const resizeLogo = (file) =>
    new Promise((resolve) => {
      const MAX_W = 240, MAX_H = 80;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(MAX_W / img.width, MAX_H / img.height, 1);
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });

  // Dodawanie nowej firmy – nowa firma domyślnie ma dostepny: true
  const handleSubmit = async () => {
    const newFirma = {
      nazwa,
      kodpocztowy,
      miasto,
      ulica,
      numer,
      nip,
      regon,
      dostepny: true,
    };
    if (firmy.some((f) => f.nip === nip)) {
      alert("Firma o podanym NIP już istnieje");
      return;
    }
    try {
      const response = await axios.post(`${baseURL}api/firmaAdd`, newFirma, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let finalFirma = response.data;
      if (logoPreview) {
        const logoRes = await axios.put(
          `${baseURL}api/firmy/${finalFirma.id}/logo`,
          { logo: logoPreview },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalFirma = logoRes.data;
      }
      setNazwa("");
      setKodpocztowy("");
      setMiasto("");
      setUlica("");
      setNumer("");
      setNip("");
      setRegon("");
      setLogoPreview(null);
      setFirmy([...firmy, finalFirma]);
      onAddSuccess();
    } catch (error) {
      console.error("Błąd dodawania firmy:", error);
    }
  };

  useEffect(() => {
    axios
      .get(`${baseURL}api/firmy`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => setFirmy(response.data))
      .catch((error) => console.error("Błąd pobierania firm:", error));
  }, [token]);

  // Zamiast usuwania, zmieniamy status dostępności – w trybie edycji będziemy mogli zmieniać dostępność\n
  const handleToggleAvailability = (firma) => {
    if (editingFirma && editingFirma.id === firma.id) {
      setEditingFirma({ ...editingFirma, dostepny: !editingFirma.dostepny });
    }
  };

  // Rozpoczęcie edycji rekordu firmy
  const handleEditClick = (firma) => {
    setEditingFirma({ ...firma });
    setEditingLogoPreview(null);
  };

  // Zapis zmian rekordu (w tym zmiana statusu dostępności)
  const handleSaveClick = async () => {
    try {
      const response = await axios.put(
        `${baseURL}api/firmy/${editingFirma.id}`,
        editingFirma,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let updatedFirma = response.data;
      if (editingLogoPreview === "__remove__") {
        const logoRes = await axios.put(
          `${baseURL}api/firmy/${editingFirma.id}/logo`,
          { logo: null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updatedFirma = logoRes.data;
      } else if (editingLogoPreview) {
        const logoRes = await axios.put(
          `${baseURL}api/firmy/${editingFirma.id}/logo`,
          { logo: editingLogoPreview },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updatedFirma = logoRes.data;
      }
      setFirmy(firmy.map((f) => (f.id === editingFirma.id ? updatedFirma : f)));
      setEditingFirma(null);
      setEditingLogoPreview(null);
      toast.current.show({
        severity: "success",
        summary: "Sukces",
        detail: "Firma zaktualizowana",
      });
    } catch (error) {
      console.error("Błąd aktualizacji firmy:", error);
    }
  };

  const handleCancelClick = () => {
    setEditingFirma(null);
    setEditingLogoPreview(null);
    toast.current.show({
      severity: "info",
      summary: "Anulowano",
      detail: "Anulowano edycję firmy",
    });
  };

  // Renderowanie edytowalnych komórek lub zwykłych
  const textBodyTemplate = (rowData, field) => {
    if (editingFirma && editingFirma.id === rowData.id) {
      return (
        <InputText
          value={editingFirma[field] || ""}
          onChange={(e) =>
            setEditingFirma({ ...editingFirma, [field]: e.target.value })
          }
        />
      );
    }
    return rowData[field];
  };

  // Kolumna "Dostępny" – checkbox aktywny tylko w trybie edycji, poza tym wyświetlany jako disabled
  const availabilityBodyTemplate = (rowData) => {
    if (editingFirma && editingFirma.id === rowData.id) {
      return (
        <Checkbox
          checked={editingFirma.dostepny}
          onChange={() => handleToggleAvailability(rowData)}
        />
      );
    } else {
      return <Checkbox checked={rowData.dostepny} disabled />;
    }
  };

  // Kolumna z przyciskami edycji – podczas edycji przyciski zapisu/ anulowania
  const actionBodyTemplate = (rowData) => {
    if (editingFirma && editingFirma.id === rowData.id) {
      return (
        <React.Fragment>
          <Button
            icon="pi pi-save"
            className="p-button-success p-mr-2"
            onClick={handleSaveClick}
          />
          <Button
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={handleCancelClick}
          />
        </React.Fragment>
      );
    } else {
      return (
        <Button
          // label="Edytuj"
          onClick={() => handleEditClick(rowData)} 
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
        // className="p-button-warning"
        />
      );
    }
  };

  const logoBodyTemplate = (rowData) => {
    if (editingFirma && editingFirma.id === rowData.id) {
      const currentLogo = editingLogoPreview || rowData.logo;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
          {currentLogo ? (
            <div style={{ position: "relative", display: "inline-flex" }}>
              <img
                src={currentLogo}
                alt="logo"
                style={{ maxHeight: 40, maxWidth: 120, objectFit: "contain", borderRadius: 4, border: "1px solid #e2e8f0" }}
              />
              <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-text p-button-sm p-button-danger"
                style={{ position: "absolute", top: -8, right: -8, width: 20, height: 20, padding: 0 }}
                onClick={() => setEditingLogoPreview("__remove__")}
                tooltip="Usuń logo"
              />
            </div>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: "11px" }}>brak logo</span>
          )}
          <input
            ref={editingLogoInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={async (e) => {
              if (e.target.files[0]) {
                const resized = await resizeLogo(e.target.files[0]);
                setEditingLogoPreview(resized);
                e.target.value = "";
              }
            }}
          />
          <Button
            icon="pi pi-image"
            label="Zmień logo"
            className="p-button-outlined p-button-sm"
            style={{ fontSize: "11px", padding: "3px 8px" }}
            onClick={() => editingLogoInputRef.current?.click()}
          />
        </div>
      );
    }
    if (rowData.logo) {
      return (
        <img
          src={rowData.logo}
          alt="logo"
          style={{ maxHeight: 36, maxWidth: 100, objectFit: "contain" }}
        />
      );
    }
    return <span style={{ color: "#aaa", fontSize: "12px" }}>—</span>;
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="add-form">
        <h3>Dodaj firmę</h3>
        <div className="add-input">
          <label>Nazwa firmy:</label>
          <InputText value={nazwa} onChange={(e) => setNazwa(e.target.value)} />
        </div>
        <div className="add-input">
          <label>Kod pocztowy:</label>
          <InputMask
            value={kodpocztowy}
            onChange={(e) => setKodpocztowy(e.target.value)}
            mask="99-999"
          />
        </div>
        <div className="add-input">
          <label>Miasto:</label>
          <InputText
            value={miasto}
            onChange={(e) => setMiasto(e.target.value)}
          />
        </div>
        <div className="add-input">
          <label>Ulica:</label>
          <InputText value={ulica} onChange={(e) => setUlica(e.target.value)} />
        </div>
        <div className="add-input">
          <label>Numer:</label>
          <InputText value={numer} onChange={(e) => setNumer(e.target.value)} />
        </div>
        <div className="add-input">
          <label>NIP:</label>
          <InputMask
            value={nip}
            onChange={(e) => setNip(e.target.value)}
            mask="999-999-99-99"
          />
        </div>
        <div className="add-input">
          <label>REGON:</label>
          <InputText value={regon} onChange={(e) => setRegon(e.target.value)} />
        </div>
        <div className="add-input">
          <label>Logo firmy:</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={async (e) => {
                if (e.target.files[0]) {
                  const resized = await resizeLogo(e.target.files[0]);
                  setLogoPreview(resized);
                  e.target.value = "";
                }
              }}
            />
            <Button
              icon="pi pi-image"
              label={logoPreview ? "Zmień logo" : "Wybierz logo"}
              className="p-button-outlined p-button-secondary p-button-sm"
              type="button"
              onClick={() => logoInputRef.current?.click()}
            />
            {logoPreview && (
              <>
                <img
                  src={logoPreview}
                  alt="podgląd logo"
                  style={{ maxHeight: 36, maxWidth: 100, objectFit: "contain", border: "1px solid #e2e8f0", borderRadius: 4 }}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-rounded p-button-text p-button-danger p-button-sm"
                  onClick={() => setLogoPreview(null)}
                  tooltip="Usuń"
                />
              </>
            )}
          </div>
        </div>
        <Button
          label="Dodaj firmę"
          onClick={handleSubmit}
          disabled={!nip || !nazwa || !miasto || !ulica || !numer || !regon}
        />
      </div>
      <Button
        label={showAll ? "Pokaż tylko dostępne" : "Pokaż wszystkie"}
        onClick={() => setShowAll(!showAll)}
        style={{ margin: "1rem 0" }}
      />
      {isMobile && (
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <i>
            W celu uzyskania lepszej widoczności danych, skorzystaj z orientacji
            poziomej
          </i>
        </div>
      )}
      <DataTable
        value={showAll ? firmy : firmy.filter((f) => f.dostepny)}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 15]}
        selectionMode="single"
        style={{ marginTop: "30px" }}
      >
        <Column
          header="Logo"
          body={logoBodyTemplate}
          headerStyle={{ width: "8rem", textAlign: "center" }}
          bodyStyle={{ textAlign: "center" }}
        />
        <Column
          header="Nazwa"
          field="nazwa"
          sortable
          body={(rowData) => textBodyTemplate(rowData, "nazwa")}
        />
        <Column header="Kod pocztowy" field="kodpocztowy" sortable />
        <Column header="Miasto" field="miasto" sortable />
        <Column header="Ulica" field="ulica" sortable />
        <Column header="Numer" field="numer" sortable />
        <Column header="NIP" field="nip" sortable />
        <Column header="REGON" field="regon" sortable />
        <Column
          header="Dostępny"
          field="dostepny"
          sortable
          body={availabilityBodyTemplate}
          headerStyle={{ textAlign: "center", width: "7rem" }}
          bodyStyle={{ textAlign: "center" }}
        />
        {user?.role === "Administrator" && (
          <Column header="Akcje" body={actionBodyTemplate} />
        )}
      </DataTable>
    </div>
  );
};

export default AddFirma;
